export const PROJECT_PHASES = [
  "initiation",
  "development",
  "promotion",
  "production",
] as const;

export type ProjectPhase = (typeof PROJECT_PHASES)[number];
export type ProjectPhasePermissions = Record<ProjectPhase, boolean>;
export type ProjectPhaseState =
  | "not_started"
  | "in_progress"
  | "blocked"
  | "completed";
export type ProjectProgressStepStatus =
  | "wait"
  | "process"
  | "error"
  | "finish";

export interface ProjectPhaseSummary {
  total: number;
  completed: number;
  pending: number;
  blocked: number;
  is_completed: boolean;
  state: ProjectPhaseState;
}

export type ProjectPhaseSummaries = Record<
  ProjectPhase,
  ProjectPhaseSummary
>;

export interface ProjectFlowLayout {
  version: number;
  nodes: Record<string, { x: number; y: number }>;
  updated_at?: number;
}

export type ProjectFlowLayouts = Record<ProjectPhase, ProjectFlowLayout>;

export interface ProjectPhaseRuntimeFields {
  phase_permissions: ProjectPhasePermissions;
  phase_summaries: ProjectPhaseSummaries;
  flow_layouts: ProjectFlowLayouts;
  flow_graph_versions: Record<ProjectPhase, number>;
}

export const PROJECT_PHASE_LABELS: Record<ProjectPhase, string> = {
  initiation: "立项阶段",
  development: "研发阶段",
  promotion: "宣发阶段",
  production: "生产阶段",
};

export const PROJECT_PROGRESS_STEP_NAMES = [
  "立项阶段",
  "研发阶段",
  "宣发阶段",
  "生产阶段",
  "完成归档",
];

export function createEmptyPhaseSummaries(): ProjectPhaseSummaries {
  return PROJECT_PHASES.reduce((result, phase) => {
    result[phase] = {
      total: 0,
      completed: 0,
      pending: 0,
      blocked: 0,
      is_completed: false,
      state: "not_started",
    };
    return result;
  }, {} as ProjectPhaseSummaries);
}

export function normalizeProjectPhaseRuntimeFields<T extends object>(
  project: T & Partial<ProjectPhaseRuntimeFields>
): T & ProjectPhaseRuntimeFields {
  const phasePermissions = {} as ProjectPhasePermissions;
  const phaseSummaries = {} as ProjectPhaseSummaries;
  const flowLayouts = {} as ProjectFlowLayouts;
  const flowGraphVersions = {} as Record<ProjectPhase, number>;

  PROJECT_PHASES.forEach((phase) => {
    const summary = project.phase_summaries?.[phase];
    const layout = project.flow_layouts?.[phase];
    phasePermissions[phase] = project.phase_permissions?.[phase] === true;
    const total = Number.isFinite(summary?.total) ? summary!.total : 0;
    const completed = Number.isFinite(summary?.completed)
      ? summary!.completed
      : 0;
    const pending = Number.isFinite(summary?.pending) ? summary!.pending : 0;
    const blocked = Number.isFinite(summary?.blocked) ? summary!.blocked : 0;
    const isCompleted =
      pending === 0 && summary?.is_completed === true;
    phaseSummaries[phase] = {
      total,
      completed,
      pending,
      blocked,
      is_completed: isCompleted,
      state: isCompleted
        ? "completed"
        : blocked > 0
        ? "blocked"
        : total > 0
        ? "in_progress"
        : "not_started",
    };
    flowLayouts[phase] = {
      version:
        Number.isInteger(layout?.version) && Number(layout?.version) >= 0
          ? Number(layout?.version)
          : 0,
      nodes:
        layout?.nodes && typeof layout.nodes === "object" ? layout.nodes : {},
      ...(Number.isFinite(layout?.updated_at)
        ? { updated_at: layout!.updated_at }
        : {}),
    };
    flowGraphVersions[phase] =
      Number.isInteger(project.flow_graph_versions?.[phase]) &&
      Number(project.flow_graph_versions?.[phase]) >= 0
        ? Number(project.flow_graph_versions?.[phase])
        : 0;
  });

  const latestStartedPhaseIndex = PROJECT_PHASES.reduce(
    (latestIndex, phase, index) =>
      phaseSummaries[phase].total > 0 ? index : latestIndex,
    -1
  );
  PROJECT_PHASES.forEach((phase, index) => {
    const summary = phaseSummaries[phase];
    if (index < latestStartedPhaseIndex && summary.total === 0) {
      summary.is_completed = true;
      summary.state = "completed";
    }
  });

  return {
    ...project,
    phase_permissions: phasePermissions,
    phase_summaries: phaseSummaries,
    flow_layouts: flowLayouts,
    flow_graph_versions: flowGraphVersions,
  };
}

export function getProjectProgressStep(
  summaries: ProjectPhaseSummaries,
  archived: boolean
) {
  if (archived) {
    return PROJECT_PHASES.length;
  }
  const firstIncompleteIndex = PROJECT_PHASES.findIndex(
    (phase) => !summaries[phase].is_completed
  );
  return firstIncompleteIndex < 0
    ? PROJECT_PHASES.length
    : firstIncompleteIndex;
}

export function canArchiveByPhaseSummaries(
  summaries: ProjectPhaseSummaries
) {
  return PROJECT_PHASES.every((phase) => summaries[phase].is_completed);
}

export function getProjectProgressStepStatuses(
  summaries: ProjectPhaseSummaries,
  archived: boolean
): ProjectProgressStepStatus[] {
  const phaseStatuses = PROJECT_PHASES.map((phase) => {
    const state = summaries[phase].state;
    return state === "completed"
      ? "finish"
      : state === "blocked"
      ? "error"
      : state === "in_progress"
      ? "process"
      : "wait";
  }) as ProjectProgressStepStatus[];
  return [
    ...phaseStatuses,
    archived
      ? "finish"
      : canArchiveByPhaseSummaries(summaries)
      ? "process"
      : "wait",
  ];
}

export function getProjectTaskListBody(
  projectId: string,
  phase: ProjectPhase
) {
  return {
    project_id: projectId,
    phase,
    pageIndex: 0,
    pageSize: 1000,
  };
}

export function getOngoingProjects<
  T extends { status: "planning" | "active" | "blocked" | "done" }
>(projects: readonly T[]): T[] {
  return projects.filter((project) => project.status !== "done");
}

export function getProjectFlowCollaborationPath(
  projectId: string,
  phase: ProjectPhase
) {
  return `/project-flow-collaboration/${encodeURIComponent(
    projectId
  )}/${phase}`;
}
